import { useLayoutEffect, useState } from 'react';

/**
 * Hook for calculating dynamic popover dimensions based on available viewport space.
 * Determines whether to open above or below the anchor element and calculates optimal
 * width and height. Positioning is delegated to MUI's native `anchorOrigin` API.
 *
 * @example
 * ```typescript
 * const { maxHeight, openAbove, width } =
 *   usePopoverDimensions(anchorEl, { marginThresholdPx: 8 });
 * ```
 */
export function usePopoverDimensions(
  anchorEl: HTMLElement | null,
  options: {
    marginThresholdPx: number;
    defaultHeightPx?: number;
  }
) {
  const { defaultHeightPx = 500, marginThresholdPx } = options;
  const [state, setState] = useState({
    maxHeight: defaultHeightPx,
    openAbove: false,
    width: 0,
  });

  useLayoutEffect(() => {
    if (!anchorEl) return;

    const updateDimensions = () => {
      const { bottom, top, width } = anchorEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceAbove = top - marginThresholdPx * 2;
      const spaceBelow = viewportHeight - bottom - marginThresholdPx * 2;
      const shouldOpenAbove = spaceBelow < defaultHeightPx && spaceAbove > spaceBelow;
      const availableSpace = shouldOpenAbove ? spaceAbove : spaceBelow;
      const calculatedHeight = Math.max(240, Math.min(availableSpace, defaultHeightPx));

      setState({
        maxHeight: calculatedHeight,
        openAbove: shouldOpenAbove,
        width,
      });
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(updateDimensions);

    resizeObserver.observe(anchorEl);
    window.addEventListener('resize', updateDimensions);
    window.addEventListener('scroll', updateDimensions, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('scroll', updateDimensions, true);
    };
  }, [anchorEl, defaultHeightPx, marginThresholdPx]);

  return state;
}
